/**
 * CRM-LIST-VIEWS-V1 (V1-P7) -- ColumnChooser: the reusable column-visibility
 * primitive (registered shared component; token layer only; C-10).
 *
 * The consumer supplies ONLY the caller-LAWFUL columns (its registry's
 * role-shaped cut -- an unauthorized column is never offered here, cannot be
 * restored by URL editing, and its data is structurally absent from the
 * wire; tier shaping precedes rendering). This component renders checkboxes
 * and reports the chosen id set; "Default" restores the role default.
 */

import { useEffect, useRef, useState } from 'react';

export interface ChooserColumn {
  readonly id: string;
  readonly label: string;
}

export interface ColumnChooserProps {
  /** The caller-lawful columns, registry order. */
  readonly columns: readonly ChooserColumn[];
  /** Currently visible ids (registry order). */
  readonly visible: readonly string[];
  readonly onToggle: (id: string, on: boolean) => void;
  /** Restore the role-default column set. */
  readonly onDefault: () => void;
}

export default function ColumnChooser({
  columns,
  visible,
  onToggle,
  onDefault,
}: ColumnChooserProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent): void => {
      if (ref.current !== null && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);
  return (
    <div className="ac-colchooser" ref={ref}>
      <button
        type="button"
        className="ac-colchooser__trigger"
        aria-haspopup="true"
        aria-expanded={open}
        data-testid="ac-colchooser-trigger"
        onClick={() => setOpen((v) => !v)}
      >
        Columns
      </button>
      {open ? (
        <div className="ac-colchooser__menu" role="menu" data-testid="ac-colchooser-menu">
          {columns.map((c) => {
            const on = visible.includes(c.id);
            return (
              <label key={c.id} className="ac-colchooser__option">
                <input
                  type="checkbox"
                  checked={on}
                  // The last visible column cannot be removed (a zero-column
                  // table is never a lawful render state).
                  disabled={on && visible.length === 1}
                  data-testid={`ac-colchooser-option-${c.id}`}
                  onChange={() => onToggle(c.id, !on)}
                />
                {c.label}
              </label>
            );
          })}
          <button
            type="button"
            className="ac-colchooser__default"
            data-testid="ac-colchooser-default"
            onClick={onDefault}
          >
            Default columns
          </button>
        </div>
      ) : null}
    </div>
  );
}
